import { EventLoadTypes } from '@racemap/sdk/schema/events';
import type { EventInfo } from '@racemap/sdk/schema/player';
import { RacemapAPIClient, sortedUniqueIds } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import {
  OneDayInMillis,
  OneHourInMillis,
  OneMinuteInMillis,
  OneSecondInMillis,
} from '@racemap/utilities/consts/time';
import { getLoadSource } from '../loadSource';
import ChunkManager from './ChunkManager';
import { shouldRebuildChunkManager } from './chunkManagerReuse';
import { type HashContent, parseHash } from './controller/HashManager';
import { hasEventDataChanged } from './hasEventDataChanged';
import type { EventStateWithOptionsAndNulledChangeable } from './parse';
import { type EventStateWithOptions, parseChunks, parseEvent } from './parse';
import type { ChunkInfo, EventChangeableState, NetReplayModule } from './store';
import { useStore } from './store';
import { longestCommonPrefix, type Mutable, queryize, sleep } from './utils';
import Option from './utils/option';

const CDN_HOST = import.meta.env.VITE_CDN_HOST;
const apiClient = new RacemapAPIClient(CDN_HOST, { includeCredentials: false });
let reloadInFlight: Promise<void> | null = null;

// The timeout covers a map that never loads, e.g. without WebGL
const MAP_LOAD_TIMEOUT = 5 * OneSecondInMillis;
let markMapLoadedOnce: () => void = () => {};
const mapLoaded = new Promise<void>((resolve) => {
  markMapLoadedOnce = resolve;
});

export function markMapLoaded(): void {
  markMapLoadedOnce();
}

export function waitForMap(): Promise<void> {
  return Promise.race([mapLoaded, sleep(MAP_LOAD_TIMEOUT)]);
}

function calculateChunkSize(startersCount: number): number {
  if (startersCount > 10000) {
    return 10 * OneMinuteInMillis;
  }
  if (startersCount > 1000) {
    return OneHourInMillis;
  }
  return OneDayInMillis;
}

function getJSON<U>(url: string): Promise<U> {
  return fetch(url).then((res) => res.json());
}
async function getArrayBufferOpt(url: string): Promise<Option<ArrayBuffer>> {
  const res = await fetch(url);
  if (res.status === 200) {
    return Option.of(await res.arrayBuffer());
  }
  return Option.none();
}

function generateShortNames(events: Array<EventStateWithOptions>): Array<EventStateWithOptions> {
  const prefix = longestCommonPrefix(events.map((event) => event.shortName));
  return events.map((event) => ({
    ...event,
    shortName: event.shortName.substring(prefix.length),
  }));
}

// fire and forget — the callers don't await, so a failed beacon must not surface as an
// unhandled rejection in the player
export async function triggerMapLoad(eventId: string): Promise<void> {
  await apiClient
    .triggerEventLoad(eventId, { loadType: EventLoadTypes.MAP_LOADS, source: getLoadSource() })
    .catch(() => {});
}

// This is only a workaround for the elevation chart. In the future the elevation chart should
// trigger the load type 'elevation-chart'
export async function triggerLeaderboardLoad(eventId: string): Promise<void> {
  await apiClient
    .triggerEventLoad(eventId, { loadType: EventLoadTypes.TIMING_LOADS, source: getLoadSource() })
    .catch(() => {});
}

export async function getPointsBuffer(
  host: string,
  eventId: string,
  options: {
    chunkSize?: number;
    netReplay?: NetReplayModule;
    range?: [Date, Date];
    starterIds?: Array<string>;
  } = {},
): Promise<Option<ArrayBuffer>> {
  const opts: {
    chunk_size?: string;
    net_replay?: string;
    range?: string;
    starter_ids?: string;
    offset_mode?: string;
  } = { offset_mode: 'binarized' };
  if (options.chunkSize != null) {
    opts.chunk_size = String(options.chunkSize);
  }
  if (options.netReplay?.enabled) {
    opts.net_replay = `${options.netReplay.syncOffset},${options.netReplay.syncTime.toISOString()}`;
  }
  if (options.range != null) {
    opts.range = `${options.range[0].toISOString()}--${options.range[1].toISOString()}`;
  }
  if (options.starterIds != null) {
    opts.starter_ids = sortedUniqueIds(options.starterIds).join(',');
  }

  const url = queryize(`${host}/api/events/${eventId}/points.bin`, opts);
  return getArrayBufferOpt(url);
}

type EventDataPair = {
  eventData: EventStateWithOptionsAndNulledChangeable;
  chunkInfos: Array<ChunkInfo> | Promise<Array<ChunkInfo>>;
  reusedChangeable: EventChangeableState | null;
};

async function loadEventData(
  eventId: string,
  existingChangeable: Array<EventChangeableState> | undefined,
  beforeParse: Promise<void>,
  beforePoints: () => Promise<void>,
): Promise<Array<EventDataPair>> {
  const hash = parseHash(window.location.hash);
  let eventInfos = await getJSON<EventInfo>(`${CDN_HOST}/api/player/${eventId}/info`);

  if (window.location.search.includes('app=true')) {
    // Reload without cache if the selected ids cannot be found in any events' starter list
    if (hash.selected != null && hash.selected.length > 0) {
      if (
        hash.selected.some(
          (selectedId) =>
            !eventInfos.some((event) =>
              event.starters.some((starter) => selectedId === starter.id),
            ),
        )
      ) {
        eventInfos = await getJSON<EventInfo>(
          `${CDN_HOST}/api/player/${eventId}/info?nocache=${sortedUniqueIds(hash.selected).join(',')}`,
        );
      }
    }
  }

  const existingByEventId = new Map(existingChangeable?.map((c) => [c.eventId, c]) ?? []);

  return await Promise.all(
    eventInfos.map(async (eventInfo, i) => {
      const existing = existingByEventId.get(eventInfo.id);

      // Check if we can reuse an existing ChunkManager
      if (existing != null) {
        await beforeParse;
        // Parse without shadow track first for a lightweight comparison
        let eventData = parseEvent(
          i,
          eventInfo,
          existing.chunkManager.chunkedShadowTrack != null
            ? Option.of(existing.chunkManager.chunkedShadowTrack.toArrayBuffer())
            : Option.none(),
        );
        eventData = overrideWithHash(eventData, hash);

        const { needsRebuild, shadowTrackChanged } = shouldRebuildChunkManager(
          {
            starterIds: existing.chunkManager.starterIds,
            shadowgeojsonHash: existing.shadowgeojsonHash,
            startTime: existing.startTime,
            endTime: existing.endTime,
            netReplay: existing.netReplay,
          },
          {
            starters: eventData.starters,
            shadowgeojsonHash: eventData.shadowgeojsonHash,
            startTime: eventData.startTime,
            endTime: eventData.endTime,
            netReplay: eventData.modules.netReplay,
          },
        );

        if (!needsRebuild) {
          return {
            eventData: {
              ...eventData,
              chunkedShadowTrack: existing.chunkManager.chunkedShadowTrack,
            },
            chunkInfos: [],
            reusedChangeable: existing,
          };
        }

        // Load shadow track only if hash changed, otherwise reuse existing
        if (shadowTrackChanged) {
          const chunkedShadowTrack = await getArrayBufferOpt(
            `${CDN_HOST}/api/events/${eventInfo.id}/geo/shadow.chunked.bin`,
          );
          eventData = parseEvent(i, eventInfo, chunkedShadowTrack);
          eventData = overrideWithHash(eventData, hash);
        } else {
          eventData = {
            ...eventData,
            chunkedShadowTrack: existing.chunkManager.chunkedShadowTrack,
          };
        }

        const buffer = await getPointsBuffer(CDN_HOST, eventData.eventId, {
          chunkSize: calculateChunkSize(eventData.starters.length),
          netReplay: eventData.modules.netReplay,
          range: [new Date(eventData.startTime), new Date(eventData.endTime)],
        });
        const chunkInfos = buffer.map(parseChunks).getOrElse(() => []);

        return { eventData, chunkInfos, reusedChangeable: null };
      }

      // Initial load: points are not awaited, so the map renders before they arrive
      // parseEvent reads correctedNow() for repeating events and missing start or end times
      const [chunkedShadowTrack] = await Promise.all([
        getArrayBufferOpt(`${CDN_HOST}/api/events/${eventInfo.id}/geo/shadow.chunked.bin`),
        beforeParse,
      ]);
      let eventData = parseEvent(i, eventInfo, chunkedShadowTrack);
      eventData = overrideWithHash(eventData, hash);

      const { startTime, endTime, starters, modules } = eventData;
      const chunkInfos = beforePoints()
        .then(() =>
          getPointsBuffer(CDN_HOST, eventInfo.id, {
            chunkSize: calculateChunkSize(starters.length),
            netReplay: modules.netReplay,
            range: [new Date(startTime), new Date(endTime)],
          }),
        )
        .then((buffer) => buffer.map(parseChunks).getOrElse(() => []));

      return { eventData, chunkInfos, reusedChangeable: null };
    }),
  );
}

type LoadDataHooks = {
  /** Holds parsing back, e.g. until the clock is calibrated. The downloads do not wait. */
  beforeParse?: Promise<void>;
  /** Holds the initial points download back, e.g. until the map tiles are in. */
  beforePoints?: () => Promise<void>;
};

/**
 * Resolves before the initial points arrive; they are added to each ChunkManager later.
 */
export async function loadData(
  eventId: string,
  existingChangeable?: Array<EventChangeableState>,
  { beforeParse = Promise.resolve(), beforePoints = () => Promise.resolve() }: LoadDataHooks = {},
): Promise<Array<EventStateWithOptions>> {
  const eventDataPairs = await loadEventData(
    eventId,
    existingChangeable,
    beforeParse,
    beforePoints,
  );
  const events: Array<EventStateWithOptions> = [];
  for (const { eventData, chunkInfos, reusedChangeable } of eventDataPairs) {
    if (reusedChangeable != null) {
      events.push({ ...eventData, changeable: reusedChangeable });
    } else {
      const chunkManager = ChunkManager.create(
        eventData.eventId,
        Array.isArray(chunkInfos) ? chunkInfos : [],
        eventData.starters,
        eventData.chunkedShadowTrack,
      );
      if (!Array.isArray(chunkInfos)) chunkManager.fill(chunkInfos);
      // Redraw markers as soon as a chunk is decoded, also while the timer is paused
      chunkManager.onDecoded = () => useStore.setState({});
      events.push({
        ...eventData,
        changeable: {
          eventId: eventData.eventId,
          chunkManager,
          starterTracks: new Map(),
          shadowgeojsonHash: eventData.shadowgeojsonHash,
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          netReplay: eventData.modules.netReplay,
        },
      });
    }
  }
  return generateShortNames(events);
}

export async function triggerReload(eventId: string): Promise<void> {
  if (reloadInFlight != null) {
    return reloadInFlight;
  }

  reloadInFlight = (async () => {
    useStore.setState({ isReloading: true });
    try {
      const existingChangeable = useStore.getState().eventsChangeable;
      const [events, options] = await Promise.all([
        loadData(eventId, existingChangeable),
        apiClient.getPlayerBaseOptions(eventId),
      ]);

      const oldEvents = useStore.getState().events;
      if (hasEventDataChanged(oldEvents, events)) {
        useStore.getState().actions.refreshFullEventData(events);
      }

      const hashColorMatch = window.location.hash.match(/color=([A-Za-z0-9]{6})/)?.[1];
      if (hashColorMatch == null) {
        const color = options.baseColor ?? RacemapColors.PaleBlue;
        document.documentElement.style.setProperty('--base-color', color);
      }
    } finally {
      useStore.setState({ isReloading: false });
      reloadInFlight = null;
    }
  })();

  return reloadInFlight;
}

function overrideWithHash(
  eventData: EventStateWithOptionsAndNulledChangeable,
  hash: HashContent,
): EventStateWithOptionsAndNulledChangeable {
  const options = eventData.options as Mutable<EventStateWithOptions['options']>;
  const infoTabsOptions = options.infoTabs as Mutable<EventStateWithOptions['options']['infoTabs']>;
  if (hash.listOpen != null) {
    options.listOpenByDefault = hash.listOpen;
  }
  if (hash.flagContent != null) {
    options.flagContent = hash.flagContent;
  }
  if (hash.infoTabsDefaultTab != null) {
    infoTabsOptions.defaultTab = hash.infoTabsDefaultTab;
  }
  if (hash.infoTabsOpenByDefault != null) {
    infoTabsOptions.openByDefault = hash.infoTabsOpenByDefault;
  }
  if (hash.infoTabsDisabledTabs != null) {
    infoTabsOptions.disabledTabs = hash.infoTabsDisabledTabs;
  }
  return eventData;
}
